import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase'; // adjust path as needed
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const PartyMembers = ({ uid }) => {
  const [participants, setParticipants] = useState([]);
  const [originalParticipants, setOriginalParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateStatus, setUpdateStatus] = useState("");

  // Helper to compare two participant objects (ignoring UI properties)
  const isDirty = (p, original) => {
    if (!original) return false;
    const fields = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'age',
      'role',
      'instrument',
      'nationality',
      'address',
      'dietary',
      'singleBed'
    ];
    return fields.some(field => p[field] !== original[field]);
  };

  // Fetch the reservation document using the uid and extract the participants array.
  useEffect(() => {
    if (!uid) return;
    const fetchParticipants = async () => {
      try {
        const reservationRef = doc(db, 'reservations', uid);
        const reservationSnap = await getDoc(reservationRef);
        if (reservationSnap.exists()) {
          const data = reservationSnap.data();
          // Map participants with a temporary id and an "expanded" property.
          const fetchedParticipants = data.participants.map((p, index) => ({
            ...p,
            id: index + 1, // temporary id
            expanded: false,
          }));
          setParticipants(fetchedParticipants);
          // Save a copy for comparison (exclude our temporary properties)
          setOriginalParticipants(
            fetchedParticipants.map(({ id, expanded, ...rest }) => rest)
          );
        } else {
          console.log('No such document!');
        }
      } catch (error) {
        console.error('Error fetching participants:', error);
      }
      setLoading(false);
    };

    fetchParticipants();
  }, [uid]);

  // Toggle expand/collapse for a participant
  const toggleExpand = (id) => {
    setParticipants((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, expanded: !p.expanded } : p
      )
    );
  };

  // Handle editing of participant fields
  const handleChange = (id, field, value) => {
    setParticipants((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      )
    );
  };

  // Delete a participant
  const handleDeleteParticipant = (id) => {
    setParticipants((prev) =>
      prev.filter((p) => p.id !== id)
    );
  };

  // Add a new participant
  const handleAddParticipant = () => {
    const newParticipant = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      age: "",
      role: "",
      instrument: "",
      nationality: "",
      address: "",
      dietary: "",
      singleBed: false,
      id: participants.length + 1, // assign new temporary id
      expanded: true, // automatically expand new participant for editing
    };
    setParticipants([...participants, newParticipant]);
    setOriginalParticipants([...originalParticipants, {}]); // new participant is dirty by default
  };

  // Update a participant in Firestore
  const handleUpdateParticipant = async (participantId) => {
    try {
      // Update the Firestore document with the current participants array.
      // Remove temporary properties before saving.
      const updatedParticipants = participants.map(({ id, expanded, ...rest }) => rest);
      const reservationRef = doc(db, 'reservations', uid);
      await updateDoc(reservationRef, { participants: updatedParticipants });
      setUpdateStatus("Participant updated successfully!");
      // Update the originalParticipants for this participant.
      setOriginalParticipants((prev) =>
        prev.map((orig, idx) =>
          participants[idx].id === participantId
            ? (({ id, expanded, ...rest }) => rest)(participants[idx])
            : orig
        )
      );
    } catch (error) {
      console.error("Error updating participant:", error);
      setUpdateStatus("Error updating participant.");
    }
  };

  if (loading) {
    return <div className="text-white">Loading party members...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-4 text-gray-100">
      <h1 className="text-2xl font-bold mb-4">Your Party Members</h1>
      {participants.length === 0 ? (
        <p>No party members found.</p>
      ) : (
        <motion.div layout className="space-y-4">
          {participants.map((participant, index) => {
            const original = originalParticipants[index];
            const dirty = isDirty(participant, original);
            return (
              <motion.div
                layout
                key={participant.id}
                className="bg-gray-800 border rounded-lg shadow-sm overflow-hidden"
              >
                {/* Header: Display participant's name and arrow to expand */}
                <div
                  onClick={() => toggleExpand(participant.id)}
                  className="px-4 py-3 flex items-center justify-between cursor-pointer bg-gray-700"
                >
                  <h2 className="font-semibold">
                    {participant.firstName || participant.lastName
                      ? `${participant.firstName} ${participant.lastName}`.trim()
                      : `Participant ${participant.id}`}
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteParticipant(participant.id);
                      }}
                      className="px-3 py-1 text-sm rounded bg-red-600 text-white hover:bg-red-700"
                    >
                      Delete
                    </button>
                    <motion.svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      initial={false}
                      animate={{ rotate: participant.expanded ? 180 : 0 }}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </motion.svg>
                  </div>
                </div>

                <AnimatePresence>
                  {participant.expanded && (
                    <motion.div
                      layout
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="px-4 py-3 bg-gray-700"
                    >
                      <div className="space-y-2">
                        <div>
                          <label className="block font-medium">First Name:</label>
                          <input
                            type="text"
                            value={participant.firstName || ""}
                            onChange={(e) =>
                              handleChange(participant.id, 'firstName', e.target.value)
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-800 text-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block font-medium">Last Name:</label>
                          <input
                            type="text"
                            value={participant.lastName || ""}
                            onChange={(e) =>
                              handleChange(participant.id, 'lastName', e.target.value)
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-800 text-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block font-medium">Email:</label>
                          <input
                            type="email"
                            value={participant.email || ""}
                            onChange={(e) =>
                              handleChange(participant.id, 'email', e.target.value)
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-800 text-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block font-medium">Phone:</label>
                          <input
                            type="text"
                            value={participant.phone || ""}
                            onChange={(e) =>
                              handleChange(participant.id, 'phone', e.target.value)
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-800 text-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block font-medium">Age:</label>
                          <input
                            type="number"
                            value={participant.age || ""}
                            onChange={(e) =>
                              handleChange(participant.id, 'age', e.target.value)
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-800 text-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block font-medium">Role:</label>
                          <input
                            type="text"
                            value={participant.role || ""}
                            onChange={(e) =>
                              handleChange(participant.id, 'role', e.target.value)
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-800 text-gray-100"
                          />
                        </div>
                        {/* Additional fields can be added here */}
                      </div>
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => handleUpdateParticipant(participant.id)}
                          disabled={!dirty}
                          className={`px-4 py-2 rounded font-bold transition-colors ${
                            dirty
                              ? "bg-green-600 hover:bg-green-500 text-white"
                              : "bg-gray-600 text-gray-300 cursor-not-allowed"
                          }`}
                        >
                          Update
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      )}
      {updateStatus && <p className="mt-2 text-center">{updateStatus}</p>}
    </div>
  );
};

export default PartyMembers;
